import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import relativeTime from 'dayjs/plugin/relativeTime';
import duration from 'dayjs/plugin/duration';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(quarterOfYear);
dayjs.extend(relativeTime);
dayjs.extend(duration);
dayjs.extend(utc);
dayjs.extend(timezone);

const INDIA_TZ = 'Asia/Kolkata';

export const getTimeStamp = () => dayjs().format(`YYYYMMDDHH:mm:ss`);
export const getTimeStampForFileName = () => {
    const randomMinutes = Math.floor(Math.random() * 20) + 1; // adding random minutes to avoid duplicate file names
    const actualMinutes = dayjs().diff(dayjs().startOf('day'), 'minutes');
    const totalMinutes = actualMinutes + randomMinutes;
    return dayjs().format(`D MMM YYYY`) + `(${totalMinutes})`; // returns 17 Jan 2025 (1309)
};

/**
 * Adds a human-readable timestamp to filename (e.g., "file - 16 Dec 2025(834).pdf")
 */
export const addTimeStampForFileName = (fileName: string) => {
    const fileExtension = fileName.split('.').pop();
    const fileNameWithoutExtension = fileName.split('.').slice(0, -1).join('.');
    return `${fileNameWithoutExtension} - ${getTimeStampForFileName()}.${fileExtension}`;
};

export const getMonthsByYear = (numOfMonths: number) => {
    const months = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];

    let endDate = dayjs();
    let endDateMonth = endDate.month();
    let endDateYear = endDate.year();
    let startDate = dayjs().subtract(numOfMonths, 'months');
    let startDateYear = startDate.year();
    let datesObj: any = {};
    for (let i = startDateYear; i <= endDateYear; i++) {
        let monthsArr;
        if (i === endDateYear) monthsArr = months.filter((month) => parseInt(month) <= endDateMonth);
        else monthsArr = months;
        datesObj[i] = monthsArr;
    }
    return { months, datesObj, startDate, endDate };
};

export const monthsEnum: { [x: string]: { name: string; fullName: string } } = {
    '01': {
        name: 'Jan',
        fullName: 'January',
    },
    '02': {
        name: 'Feb',
        fullName: 'February',
    },
    '03': {
        name: 'Mar',
        fullName: 'March',
    },
    '04': {
        name: 'Apr',
        fullName: 'April',
    },
    '05': {
        name: 'May',
        fullName: 'May',
    },
    '06': {
        name: 'Jun',
        fullName: 'June',
    },
    '07': {
        name: 'Jul',
        fullName: 'July',
    },
    '08': {
        name: 'Aug',
        fullName: 'August',
    },
    '09': {
        name: 'Sep',
        fullName: 'September',
    },
    '10': {
        name: 'Oct',
        fullName: 'October',
    },
    '11': {
        name: 'Nov',
        fullName: 'November',
    },
    '12': {
        name: 'Dec',
        fullName: 'December',
    },
};

export const DATE_FORMATS = {
    ISO: 'YYYY-MM-DD',
    MONTH_FULL_YEAR: 'MMMM YYYY',
    MONTH_SHORT_YEAR: 'MMM YYYY',
    DATE_FORMAT_DDMMYYYY: 'DD-MM-YYYY',
    DATE_FORMAT_D_MMM_YYYY: 'D MMM YYYY',
    DATE_FORMAT_DD_MMM_YYYY: 'DD MMM YYYY',
    TIME_DATE_FORMAT_HH_MM_A_DD_MMM_YYYY: 'hh:mm A, DD MMM YYYY',
    DATE_TIME_DD_MMM_YYYY_HH_MM_A: 'DD MMM YYYY hh:mm A',
    TIME_DATE_FORMAT_H_MM_A_DD_MMMM_YYYY: 'h:mm A, DD MMMM YYYY',
    HISTORY_DRAWER: 'h:mm a, D MMM YYYY',
    CARD_COMMENT_TIMESTAMP: 'h:mm a, DD MMM YYYY',
    LIBRARY_FILE_TIMESTAMP: 'h:mm a, D MMMM YYYY',
} as const;

export function getFormattedDate(date: string, periodSelectorVariant: string | null, isFinancials = false) {
    //USED IN DATEPICKER
    if (!date || !periodSelectorVariant) return '-';

    const dateTime = dayjs(date);
    if (isFinancials) return dateTime.format('YYYY');

    switch (periodSelectorVariant) {
        case 'Monthly':
            return dateTime.format("MMM'YY");
        case 'Quarterly': {
            let fyQuarter = Math.ceil((parseInt(dateTime.format('MM')) - 3) / 3);
            fyQuarter = fyQuarter <= 0 ? 4 : fyQuarter;
            return `Q${fyQuarter}'FY${parseInt(dateTime.add(parseInt(dateTime.format('MM')) > 3 ? 1 : 0, 'years').format('YY'))}`;
        }
        case 'Yearly':
            return dateTime.format('YYYY');
        default:
            return dateTime.format("MMM'YY");
    }
}

export function initializeDatepickerDates(periodSelectorVariant: string | null) {
    const DATE_FORMAT = 'YYYY-MM-DD';
    if (!periodSelectorVariant) return [];
    //Initializing dates for non recurring metrics. (Graphs in Section 1 of Dashboard)
    const now = dayjs();
    // const now = dayjs('2023-04-01');
    const previousMonthLastDate = now.subtract(1, 'month').endOf('month');
    const monthlyStartDate = previousMonthLastDate.subtract(11, 'months').startOf('month');

    const currentQuarterStart = now.startOf('quarter');
    const previousQuarterEnd = currentQuarterStart.subtract(1, 'day');
    const quarterlyStartDate = currentQuarterStart.subtract(3, 'quarters').startOf('quarter');

    const currentFinancialYearStart =
        now.month() < 3
            ? now
                  .year(now.year() - 1)
                  .startOf('year')
                  .add(3, 'month')
            : now.startOf('year').add(3, 'month');
    const previousFinancialYearStart = currentFinancialYearStart.subtract(1, 'year');
    const previousFinancialYearEnd = currentFinancialYearStart.subtract(1, 'day');
    // const yearlyStartDate = previousFinancialYearEnd.subtract(1, 'years').startOf('month');

    let toDate = '';
    let fromDate = '';
    switch (periodSelectorVariant) {
        case 'Monthly':
            toDate = previousMonthLastDate.format(DATE_FORMAT);
            fromDate = monthlyStartDate.format(DATE_FORMAT);
            break;
        case 'Quarterly':
            toDate = now.format(DATE_FORMAT);
            fromDate = quarterlyStartDate.format(DATE_FORMAT);
            break;
        case 'Yearly':
            toDate = now.format(DATE_FORMAT);
            fromDate = previousFinancialYearStart.format(DATE_FORMAT);
            break;
    }

    return [fromDate, toDate, previousMonthLastDate.format(DATE_FORMAT)];
}

export const getFinancialHeadersForDebtSchedule = (companyFinancialsDataUpdateDate, particularsLabel = 'Particulars (Cr)') => {
    const provisionalDataHeader = `Provisional${companyFinancialsDataUpdateDate ? ` (${companyFinancialsDataUpdateDate})*` : '*'}`;
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;

    let startYear;
    startYear = currentYear - 3;

    // from january to march, we need to go one more year back
    if (currentMonth <= 3) {
        startYear = currentYear - 4;
    }

    return [
        {
            name: particularsLabel,
            key: 'classificationName',
        },
        {
            name: `FY${String(startYear + 1).slice(-2)}A`,
            key: String(startYear),
        },
        {
            name: `FY${String(startYear + 2).slice(-2)}A`,
            key: String(startYear + 1),
        },
        {
            name: `FY${String(startYear + 3).slice(-2)}A`,
            key: String(startYear + 2),
        },
        {
            name: provisionalDataHeader,
            key: 'provisional',
        },
    ];
};

export const getFinancialYear = (year: number, withPrefix = false, returnEndYear = false) => {
    const suffix = getFiscalYearSuffix(year, returnEndYear);
    return withPrefix ? `FY’${suffix}` : suffix;
};

export const getFiscalYearSuffix = (year, returnEndYear = false) => {
    if (!year || isNaN(year)) return null;

    const startYear = new Date(year, 0).getFullYear();
    const endYear = startYear + 1;
    if (returnEndYear) return endYear;
    return `${startYear.toString().slice(-2)}-${endYear.toString().slice(-2)}`;
};

export const getStartDateEndDate = () => {
    const now = dayjs();
    const currentMonth = now.month();
    const currentMonthBetweenAprilAndSeptember = currentMonth >= 3 && currentMonth <= 8;
    let startDate: string, endDate: string;

    if (currentMonthBetweenAprilAndSeptember) {
        endDate = now
            .month(2)
            .date(31)
            .year(now.year() - 1)
            .format('YYYY-MM-DD');
        startDate = now
            .month(3)
            .date(1)
            .year(now.year() - 7)
            .format('YYYY-MM-DD');
    } else {
        endDate = now.month(2).date(31).year(now.year()).format('YYYY-MM-DD');
        startDate = now
            .month(3)
            .date(1)
            .year(now.year() - 6)
            .format('YYYY-MM-DD');
    }
    return { startDate, endDate };
};

export const formatFinancialDate = (date: string) => {
    if (!date) return '';
    const [year, month, day] = date.split('-');
    const dateObj = new Date(Number(year), Number(month) - 1, Number(day));

    const options: Intl.DateTimeFormatOptions = {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    };
    return `As of ${new Intl.DateTimeFormat('en-GB', options).format(dateObj)}`;
};

export const checkIfDateIsValid = (date: Date): boolean => {
    if (!date || !(date instanceof Date)) return false;
    return dayjs(date).isValid();
};

export const formatDateString = (date: Date, displayFormat: string) => {
    if (!checkIfDateIsValid(date)) {
        return '';
    }

    if (!displayFormat || displayFormat.trim() === '') {
        return dayjs(date).format(DATE_FORMATS.ISO);
    }
    return dayjs(date).format(displayFormat);
};

export const getAuditedYear = (year: number) => {
    if (!year || isNaN(year)) return '';
    const cutoff = dayjs(`${year + 1}-03-31`);
    return dayjs().isAfter(cutoff) ? `${year + 1}` : `${year}`;
};

/**
 * Sorts an array of objects by a date field in descending order (latest first)
 * @param entries - Array of objects to sort
 * @param dateField - Name of the date field to sort by (defaults to 'lastUpdatedAt')
 * @returns Sorted array (latest first)
 */
export const sortByDateDescending = <T extends Record<string, any>>(entries: T[], dateField: string = 'lastUpdatedAt'): T[] => {
    return [...entries].sort((a, b) => {
        const dateA = a[dateField] ?? 0;
        const dateB = b[dateField] ?? 0;
        return dateB - dateA;
    });
};

/**
 * Formats a timestamp (number) or date string using the specified format
 * @param timestamp - Timestamp in milliseconds (number) or date string
 * @param format - Date format string (e.g., 'DD MMM YYYY'). Defaults to ISO format if not provided
 * @returns Formatted date string or empty string if invalid
 */
export const formatDateByFormat = (timestamp: number | string | undefined, format?: string): string => {
    if (!timestamp) return '';
    // A bare all-digit string (e.g. "1786080600000") is epoch millis, but dayjs
    // parses it as an ISO-ish string first and yields Invalid Date — coerce to
    // a number first so numeric-string and numeric epochs both work.
    const normalized = typeof timestamp === 'string' && /^\d+$/.test(timestamp) ? Number(timestamp) : timestamp;
    const date = dayjs(normalized);
    if (!date.isValid()) return '';
    return date.format(format || DATE_FORMATS.ISO);
};

/** Format an "MM-YYYY" key (e.g. "03-2025") as "Mar 2025". */
export const formatMonthYearHeader = (monthHeader: string): string => {
    if (!monthHeader?.includes('-')) return monthHeader || '';
    const [mm, yyyy] = monthHeader.split('-');
    return dayjs(`${yyyy}-${mm}-01`, DATE_FORMATS.ISO).format('MMM YYYY');
};

/** Whole years between `begin` and `end` (defaults to now). Returns `null` if `begin` is missing/invalid. */
export const getYearsBetween = (begin?: number | string, end?: number | string): number | null => {
    if (!begin) return null;
    const startDate = dayjs(begin);
    if (!startDate.isValid()) return null;
    return (end ? dayjs(end) : dayjs()).diff(startDate, 'year');
};

/** Tenure-cell display string: "-", "< 1 year", or "N+ year(s)". */
export const formatTenureYears = (begin?: number, end?: number): string => {
    const years = getYearsBetween(begin, end);
    if (years == null) return '-';
    if (years <= 0) return '< 1 year';
    return `${years}+ year${years > 1 ? 's' : ''}`;
};

type DurationUnit = { threshold: number; unit: 'day' | 'hour' | 'minute' | 'second' };

const DURATION_UNITS: DurationUnit[] = [
    { threshold: 1, unit: 'day' },
    { threshold: 1 / 24, unit: 'hour' },
    { threshold: 1 / 1440, unit: 'minute' },
    { threshold: 1 / 86400, unit: 'second' },
];

/**
 * Returns a human-readable elapsed-time string between two epoch-ms timestamps.
 * Falls back to "just now" when the difference is less than one second.
 * @param fromEpoch  Start timestamp in epoch milliseconds.
 * @param toEpoch    End timestamp in epoch milliseconds. Defaults to now.
 */
export const calculateTimeSpent = (fromEpoch: number, toEpoch?: number): string => {
    const diff = dayjs.duration(dayjs(toEpoch).diff(dayjs(fromEpoch)));
    const asDays = diff.asDays();

    for (const { threshold, unit } of DURATION_UNITS) {
        const value = Math.floor(diff.as(unit));
        if (asDays >= threshold) {
            return `${value} ${unit}${value === 1 ? '' : 's'}`;
        }
    }

    return 'just now';
};

/* 
   End year of the current Indian financial year (Apr–Mar), evaluated in IST so
   the boundary flips at midnight India time regardless of the viewer's timezone
   (e.g. 8 PM EDT on 31 Mar is already 1 Apr in India). 16 Jul 2026 → FY26-27 → 2027;
   Jan–Mar belongs to the FY that began the previous calendar year. 
*/
export const getCurrentFinancialYearEndYear = (now: dayjs.ConfigType = dayjs()): number => {
    const ist = dayjs(now).tz(INDIA_TZ);
    return ist.month() >= 3 ? ist.year() + 1 : ist.year();
};
